import type { FirstWeekContainsDate, Interval, Locale } from 'date-fns'
import {
    addDays as dateFnsAddDays,
    addHours as dateFnsAddHours,
    addMinutes as dateFnsAddMinutes,
    addMonths as dateFnsAddMonths,
    addSeconds as dateFnsAddSeconds,
    addYears as dateFnsAddYears,
    areIntervalsOverlapping as dateFnsAreIntervalsOverlapping,
    compareAsc as dateFnsCompareAsc,
    compareDesc as dateFnsCompareDesc,
    differenceInDays as dateFnsDifferenceInDays,
    differenceInHours as dateFnsDifferenceInHours,
    differenceInSeconds as dateFnsDifferenceInSeconds,
    eachDayOfInterval as dateFnsEachDayOfInterval,
    endOfDay as dateFnsEndOfDay,
    endOfMonth as dateFnsEndOfMonth,
    endOfWeek as dateFnsEndOfWeek,
    endOfYear as dateFnsEndOfYear,
    format as dateFnsFormat,
    getTime as dateFnsGetTime,
    isAfter as dateFnsIsAfter,
    isBefore as dateFnsIsBefore,
    isSameDay as dateFnsIsSameDay,
    isValid as dateFnsIsValid,
    max as dateFnsMax,
    set as dateFnsSet,
    setHours as dateFnsSetHours,
    setMinutes as dateFnsSetMinutes,
    setMonth as dateFnsSetMonth,
    setSeconds as dateFnsSetSeconds,
    setWeek as dateFnsSetWeek,
    setYear as dateFnsSetYear,
    startOfDay as dateFnsStartOfDay,
    startOfMonth as dateFnsStartOfMonth,
    startOfWeek as dateFnsStartOfWeek,
    startOfYear as dateFnsStartOfYear,
    subDays as dateFnsSubDays,
} from 'date-fns'
import { de, enUS, nl } from 'date-fns/locale'

import { Period } from '@/entities/Period/Period'
import { Weekday } from '@/types'

import { getLocale, Locale as AppLocale, trans } from './trans'

// The date-fns locale of the active app language, for locale-aware formatting and relative times
export const getDateFnsLocale = (): Locale => {
    switch (getLocale()) {
        case AppLocale.en:
            return enUS
        case AppLocale.de:
            return de
        case AppLocale.nl:
        default:
            return nl
    }
}

export const startOfDayInHours = 4

export const addDays = (date: number | Date, amount: number): Date => dateFnsAddDays(date, amount)
export const addHours = (date: number | Date, amount: number): Date => dateFnsAddHours(date, amount)
export const addMinutes = (date: number | Date, amount: number): Date => dateFnsAddMinutes(date, amount)
export const addMonths = (date: number | Date, amount: number): Date => dateFnsAddMonths(date, amount)
export const addSeconds = (date: number | Date, amount: number): Date => dateFnsAddSeconds(date, amount)
export const addYears = (date: number | Date, amount: number): Date => dateFnsAddYears(date, amount)
export const areIntervalsOverlapping = (intervalLeft: Interval, intervalRight: Interval, options?: {
    inclusive?: boolean
}): boolean => {
    try {
        return dateFnsAreIntervalsOverlapping(intervalLeft, intervalRight, options)
    } catch {
        console.error('[areIntervalsOverlapping]', intervalLeft, intervalRight)
        return false
    }
}
export const compareAsc = (dateLeft: number | Date, dateRight: number | Date): number => dateFnsCompareAsc(dateLeft, dateRight)
export const compareDesc = (dateLeft: number | Date, dateRight: number | Date): number => dateFnsCompareDesc(dateLeft, dateRight)
export const compareTimeStringsAsc = (time1: string, time2: string): boolean => (
    (!! time1 && !! time2)
    && parseInt(time1.replace(':', ''), 10) >= parseInt(time2.replace(':', ''), 10)
)
export const differenceInDays = (dateLeft: Date | number, dateRight: Date | number): number => dateFnsDifferenceInDays(dateLeft, dateRight)
export const differenceInHours = (dateLeft: Date | number, dateRight: Date | number): number => dateFnsDifferenceInHours(dateLeft, dateRight)
export const differenceInSeconds = (dateLeft: Date | number, dateRight: Date | number): number => dateFnsDifferenceInSeconds(dateLeft, dateRight)
export const eachDayOfInterval = (interval: Interval, options?: {
    step?: number
}): Date[] => dateFnsEachDayOfInterval(interval, options)
export const endOfMonth = (date: Date): Date => dateFnsEndOfMonth(date)
export const endOfYear = (date: Date): Date => dateFnsEndOfYear(date)
export const endOfWeek = (date: Date, options?: {
    locale?: Locale
    weekStartsOn?: Weekday
}): Date => dateFnsEndOfWeek(date, options)
export const endOfDay = (date: Date): Date => dateFnsEndOfDay(date)

export const getDaysOfWeek = (date: Date): Date[] => {
    const from = startOfWeek(date, { weekStartsOn: 1 })
    const to = endOfWeek(date, { weekStartsOn: 1 })

    return eachDayOfInterval({
        start: from,
        end: to,
    })
}

export const getDaysOfMonth = (date: Date): Date[] => {
    const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 })
    const end = endOfMonth(date)

    return eachDayOfInterval({
        start,
        end,
    })
}

export const getDaysOfYears = (date: Date): Date[] => {
    const from = startOfYear(date)
    const to = endOfYear(date)

    return eachDayOfInterval({
        start: from,
        end: to,
    })
}

export const getDaysOfPeriod = (period: Period): Date[] => eachDayOfInterval({
    start: period.start,
    end: period.end,
})

export const getTime = (date: Date): number => dateFnsGetTime(date)

// Get period minus the startOfDayInHours. So instead of 04:00 until 04:00 you get 00:00 until 00:00
export const getPeriodWithoutStartOfDayCorrection = (period: Period): Period => ({
    start: addHours(period.start, -startOfDayInHours),
    end: addHours(period.end, -startOfDayInHours),
})

export const formatDate = (date: Date, newFormat = 'dd-MM-yyyy'): string => `${dateFnsFormat(date, newFormat, { locale: nl })}`

export const isAfter = (date: Date | number, dateToCompare: Date | number): boolean => dateFnsIsAfter(date, dateToCompare)
export const isBefore = (date: Date | number, dateToCompare: Date | number): boolean => dateFnsIsBefore(date, dateToCompare)
export const isDateStringValid = (dateString: string): boolean => {
    try {
        return dateFnsIsValid(new Date(dateString))
    } catch (_e) {
        return false
    }
}
export const isValid = (date: Date): boolean => dateFnsIsValid(date)

// Returns the start of workday of date (04:00)
export const getStartOfWorkDay = (date: Date): Date => addHours(startOfDay(date), startOfDayInHours)

// Returns the end of workday of date (03:59)
export const getEndOfWorkDay = (date: Date): Date => addSeconds(addHours(endOfDay(date), startOfDayInHours), -1)

export const isStartOfWorkDay = (date: Date): boolean => getStartOfWorkDay(date).getTime() === date.getTime()

// Returns true if a date has the end of workday time,
// with a tolerance of 2 seconds because the API sometimes returns the end of the workday slightly earlier
export const isEndOfWorkDay = (date: Date): boolean => differenceInSeconds(date, getStartOfWorkDay(date)) <= 2

export const getMiddleOfDays = (days: Date[]): Date => days[Math.round(days.length / 2)]
export const max = (datesArray: (Date | number)[]): Date => dateFnsMax(datesArray)

export const setHours = (date: number | Date, amount: number): Date => dateFnsSetHours(date, amount)
export const setMinutes = (date: number | Date, amount: number): Date => dateFnsSetMinutes(date, amount)
export const setMonth = (date: number | Date, amount: number): Date => dateFnsSetMonth(date, amount)
export const setSeconds = (date: number | Date, amount: number): Date => dateFnsSetSeconds(date, amount)
export const setWeek = (
    date: number | Date,
    week: number,
    options?: { locale?: Locale, weekStartsOn?: Weekday, firstWeekContainsDate?: FirstWeekContainsDate },
): Date => dateFnsSetWeek(date, week, options)
export const setYear = (date: Date | number, year: number) => dateFnsSetYear(date, year)

export const startOfMonth = (date: Date): Date => dateFnsStartOfMonth(date)
export const startOfYear = (date: Date): Date => dateFnsStartOfYear(date)
export const startOfWeek = (date: Date, options?: {
    locale?: Locale
    weekStartsOn?: Weekday
}): Date => dateFnsStartOfWeek(date, options)
export const startOfDay = (date: Date): Date => dateFnsStartOfDay(date)

export const isMultipleDaysFromDates = (start: Date, end: Date): boolean => {
    const startHour = start.getHours()
    const endHour = end.getHours()
    return startHour === startOfDayInHours && endHour === (startOfDayInHours - 1)
}

export const hasDateFourHoursOrLess = (date: Date): boolean =>
    (date.getHours() < startOfDayInHours) || (date.getHours() === startOfDayInHours && date.getMinutes() === 0)

export const getTimeString = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
}

export const getDateTimeFromForm = (
    date: Date,
    time: string | undefined,
    multipleDays: boolean,
    isStart: boolean,
    endDate?: Date,
): Date | null => {
    if (multipleDays) {
        if (isStart) return dateFnsSet(date, { hours: startOfDayInHours, minutes: 0 })
        if (! endDate) return null
        return dateFnsSet(dateFnsAddDays(endDate, 1), { hours: startOfDayInHours - 1, minutes: 59, seconds: 58 })
    }
    if (! time) return null
    const [hours, minutes] = time.split(':').map(Number)
    return dateFnsSet(date, { hours, minutes })
}

export const formatConversationTimestamp = (date: Date, now = new Date()): string => {
    if (dateFnsIsSameDay(date, now)) {
        return dateFnsFormat(date, 'HH:mm')
    }

    if (dateFnsIsSameDay(date, dateFnsSubDays(now, 1))) {
        return trans('common.yesterday')
    }

    return new Intl.DateTimeFormat(getLocale(), { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date)
}
